import React, { useEffect, useState } from "react";
import { Buildings, FloppyDisk, Spinner, PaperPlaneTilt, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Business Profile Card — per-tenant brand identity used for outreach.
 * Renders on /profile. Also driven by the OnboardingModal for first-time users.
 */
export default function BusinessProfileCard({ onSaved }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/auth/business-profile")
      .then((r) => setProfile(r.data))
      .catch(() => setProfile({}))
      .finally(() => setLoading(false));
  }, []);

  const patch = (field, val) => setProfile((p) => ({ ...p, [field]: val }));

  const save = async () => {
    if (!profile.business_name?.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/business-profile", {
        business_name: profile.business_name || "",
        business_pitch: profile.business_pitch || "",
        business_website: profile.business_website || "",
        signature: profile.signature || "",
        reply_to_email: profile.reply_to_email || null,
      });
      toast.success("Business profile saved");
      setProfile({ ...profile, onboarding_complete: true, ...(data.user || {}) });
      onSaved?.(data.user);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testSend = async () => {
    if (!profile.business_name?.trim()) {
      toast.error("Save your business profile first");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/email/test-send");
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-6 text-center">
        <Spinner size={16} className="animate-spin mx-auto text-zinc-500" />
      </div>
    );
  }

  const isEmpty = !profile.business_name;

  return (
    <div
      data-testid="business-profile-card"
      className={`lg:col-span-2 bg-zinc-900/40 border rounded-sm p-6 ${
        isEmpty ? "border-orange-500/40" : "border-zinc-800"
      }`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Buildings size={18} weight="duotone" className="text-orange-400" />
        <div>
          <div className="font-display font-medium text-zinc-100">Business Profile</div>
          <div className="text-[11px] font-mono text-zinc-500">
            {isEmpty ? "Required — this appears on every outreach email you send" : "Used to personalize your outreach emails"}
          </div>
        </div>
        {profile.onboarding_complete && (
          <div className="ml-auto flex items-center gap-1 text-emerald-400">
            <CheckCircle size={14} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-wider">Set up</span>
          </div>
        )}
      </div>

      {isEmpty && (
        <div className="mb-4 p-3 rounded-sm bg-orange-500/[0.08] border border-orange-500/30 text-[12px] text-orange-200 flex items-start gap-2">
          <WarningCircle size={14} weight="fill" className="text-orange-400 shrink-0 mt-0.5" />
          <span>
            Your outreach emails will use fallback branding until you fill this in. Add your business name, pitch, and signature so prospects see YOUR brand.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label htmlFor="biz-name" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
            Business name <span className="text-orange-400">*</span>
          </Label>
          <Input
            id="biz-name"
            value={profile.business_name || ""}
            onChange={(e) => patch("business_name", e.target.value)}
            placeholder="Acme Web Design"
            maxLength={120}
            data-testid="biz-name-input"
            className="bg-zinc-900 border-zinc-700"
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="biz-pitch" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
            What you sell / value prop
          </Label>
          <textarea
            id="biz-pitch"
            value={profile.business_pitch || ""}
            onChange={(e) => patch("business_pitch", e.target.value)}
            placeholder="We build fast Shopify stores for pet brands. Average 40% conversion lift in 30 days."
            maxLength={500}
            rows={2}
            data-testid="biz-pitch-input"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none resize-y"
          />
          <div className="text-[10px] font-mono text-zinc-600 mt-1 text-right">{(profile.business_pitch || "").length}/500</div>
        </div>

        <div>
          <Label htmlFor="biz-web" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
            Website
          </Label>
          <Input
            id="biz-web"
            value={profile.business_website || ""}
            onChange={(e) => patch("business_website", e.target.value)}
            placeholder="https://acmeweb.co"
            maxLength={200}
            data-testid="biz-web-input"
            className="bg-zinc-900 border-zinc-700"
          />
        </div>

        <div>
          <Label htmlFor="biz-replyto" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
            Reply-to email <span className="text-zinc-600">(where prospect replies land)</span>
          </Label>
          <Input
            id="biz-replyto"
            type="email"
            value={profile.reply_to_email || ""}
            onChange={(e) => patch("reply_to_email", e.target.value)}
            placeholder={profile.email || "you@yourdomain.com"}
            data-testid="biz-replyto-input"
            className="bg-zinc-900 border-zinc-700"
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="biz-sig" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
            Email signature
          </Label>
          <textarea
            id="biz-sig"
            value={profile.signature || ""}
            onChange={(e) => patch("signature", e.target.value)}
            placeholder={`${profile.name || "Your name"}\n${profile.business_name || "Your business"}\n${profile.business_website || "your-website.com"}`}
            maxLength={500}
            rows={3}
            data-testid="biz-sig-input"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none resize-y font-mono"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-5">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="biz-save-btn"
          className="flex-1 bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium"
        >
          {saving ? <><Spinner size={14} className="animate-spin mr-2" /> Saving…</> : <><FloppyDisk size={14} weight="fill" className="mr-2" /> Save profile</>}
        </Button>
        <Button
          type="button"
          onClick={testSend}
          disabled={testing || !profile.onboarding_complete}
          variant="outline"
          data-testid="biz-test-btn"
          title={!profile.onboarding_complete ? "Save your profile first" : "Send a sample to your own inbox"}
          className="flex-1 border-zinc-700 hover:border-orange-500 hover:text-orange-300"
        >
          {testing ? <><Spinner size={14} className="animate-spin mr-2" /> Sending…</> : <><PaperPlaneTilt size={14} weight="fill" className="mr-2" /> Test my setup</>}
        </Button>
      </div>
    </div>
  );
}
